import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { triggerSave } from "../core/save/pendingSave";
import { API } from "../services/api";

const GROUPS = [
    { key: "patients", label: "Patients", abbr: "PA" },
    { key: "medicaments", label: "Médicaments", abbr: "ME" },
    { key: "commandes", label: "Commandes", abbr: "CO" },
    { key: "ehpads", label: "EHPAD", abbr: "EH" },
    { key: "bonsLivraison", label: "Bons de livraison", abbr: "BL" },
];

const EMPTY_RESULTS = Object.fromEntries(GROUPS.map(group => [group.key, []]));

function formatDate(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
}

export default function GlobalSearchPalette({ compact = false, onNavigate }) {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const requestIdRef = useRef(0);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState(EMPTY_RESULTS);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [deep, setDeep] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const flatResults = useMemo(
        () => GROUPS.flatMap(group => (results[group.key] || []).map(item => ({ ...item, groupKey: group.key }))),
        [results]
    );
    const hasResults = flatResults.length > 0;

    useEffect(() => {
        const handleKeyDown = event => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                setOpen(value => !value);
            } else if (event.key === "Escape") {
                setOpen(false);
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const frame = requestAnimationFrame(() => inputRef.current?.focus());
        return () => cancelAnimationFrame(frame);
    }, [open]);

    useEffect(() => {
        setDeep(false);
    }, [query]);

    useEffect(() => {
        const trimmed = query.trim();
        if (!open || trimmed.length < 2) {
            requestIdRef.current += 1;
            setResults(EMPTY_RESULTS);
            setLoading(false);
            setError(false);
            return undefined;
        }

        const requestId = ++requestIdRef.current;
        const timer = setTimeout(() => {
            setLoading(true);
            setError(false);
            API.get("/search", { params: { q: trimmed, deep }, noRedirectOn401: true })
                .then(({ data }) => {
                    if (requestId !== requestIdRef.current) return;
                    setResults(Object.fromEntries(GROUPS.map(group => [group.key, data?.[group.key] || []])));
                    setActiveIndex(0);
                })
                .catch(() => {
                    if (requestId !== requestIdRef.current) return;
                    setResults(EMPTY_RESULTS);
                    setError(true);
                })
                .finally(() => {
                    if (requestId === requestIdRef.current) setLoading(false);
                });
        }, deep ? 0 : 250);
        return () => clearTimeout(timer);
    }, [deep, open, query]);

    const chooseResult = async result => {
        setOpen(false);
        await triggerSave();
        navigate(result.path);
        globalThis.dispatchEvent(new CustomEvent("global-search:navigate", {
            detail: { path: result.path },
        }));
        onNavigate?.();
    };

    const handleInputKeyDown = event => {
        if (!flatResults.length) return;
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex(index => (index + 1) % flatResults.length);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex(index => (index - 1 + flatResults.length) % flatResults.length);
        } else if (event.key === "Enter") {
            event.preventDefault();
            chooseResult(flatResults[activeIndex] || flatResults[0]);
        }
    };

    let runningIndex = -1;

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                title="Recherche globale (Ctrl K)"
                aria-label="Ouvrir la recherche globale"
                style={{
                    width: compact ? "100%" : "calc(100% - 20px)",
                    height: 34,
                    margin: compact ? "0" : "8px 10px 4px",
                    padding: compact ? 0 : "0 9px",
                    border: "1px solid #5F7F79",
                    borderRadius: 8,
                    background: "rgba(244, 241, 234, 0.08)",
                    color: "#F4F1EA",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: compact ? "center" : "space-between",
                    gap: 8,
                    fontSize: 12,
                }}
            >
                <span aria-hidden="true" style={{ fontSize: 17, lineHeight: 1 }}>⌕</span>
                {!compact && <span style={{ flex: 1, textAlign: "left" }}>Rechercher</span>}
                {!compact && <kbd style={{ color: "#B9C6C2", font: "inherit", fontSize: 10 }}>Ctrl K</kbd>}
            </button>

            {open && (
                <div
                    role="presentation"
                    onMouseDown={event => { if (event.target === event.currentTarget) setOpen(false); }}
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 3000,
                        background: "rgba(12, 59, 60, 0.46)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        padding: "clamp(48px, 10vh, 96px) 14px 24px",
                    }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label="Recherche globale"
                        style={{
                            width: "min(720px, 100%)",
                            maxHeight: "min(720px, calc(100vh - 80px))",
                            background: "#fff",
                            border: "1px solid #D8D2C6",
                            borderRadius: 8,
                            boxShadow: "0 24px 70px rgba(12, 59, 60, 0.28)",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid #e2e8f0" }}>
                            <span aria-hidden="true" style={{ fontSize: 22, color: "#0C3B3C" }}>⌕</span>
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={event => setQuery(event.target.value)}
                                onKeyDown={handleInputKeyDown}
                                placeholder="Patient, médicament, commande, EHPAD ou date..."
                                aria-label="Rechercher"
                                style={{ flex: 1, minWidth: 0, border: "none", outline: "none", fontSize: 16, color: "#122A26", background: "transparent" }}
                            />
                            {loading && <span style={{ fontSize: 12, color: "#5A6D68" }}>Recherche...</span>}
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                title="Fermer"
                                aria-label="Fermer"
                                style={{ border: "none", background: "transparent", color: "#64748b", fontSize: 20, cursor: "pointer", padding: 4 }}
                            >×</button>
                        </div>

                        <div style={{ overflowY: "auto", padding: "8px 0" }}>
                            {query.trim().length < 2 && (
                                <div style={{ padding: "30px 20px", textAlign: "center", color: "#64748b", fontSize: 13 }}>
                                    Saisissez au moins 2 caractères. Les dates acceptent 23/09/2026, 23/09, 2026-09-23, aujourd’hui ou hier.
                                </div>
                            )}
                            {query.trim().length >= 2 && !loading && error && (
                                <div role="alert" style={{ padding: "30px 20px", textAlign: "center", color: "#b91c1c", fontSize: 13 }}>
                                    La recherche n’a pas pu aboutir. Réessayez dans un instant.
                                </div>
                            )}
                            {query.trim().length >= 2 && !loading && !error && !hasResults && (
                                <div style={{ padding: "30px 20px", textAlign: "center", color: "#64748b", fontSize: 13 }}>
                                    Aucun résultat pour « {query.trim()} ».
                                </div>
                            )}
                            {GROUPS.map(group => {
                                const items = results[group.key] || [];
                                if (!items.length) return null;
                                return (
                                    <section key={group.key} aria-labelledby={`search-${group.key}`}>
                                        <h2 id={`search-${group.key}`} style={{ margin: 0, padding: "9px 16px 5px", fontSize: 11, color: "#5A6D68", textTransform: "uppercase", fontWeight: 700 }}>
                                            {group.label}
                                        </h2>
                                        {items.map(item => {
                                            runningIndex += 1;
                                            const itemIndex = runningIndex;
                                            const active = itemIndex === activeIndex;
                                            return (
                                                <button
                                                    type="button"
                                                    key={`${group.key}-${item.id}-${item.path}`}
                                                    onMouseEnter={() => setActiveIndex(itemIndex)}
                                                    onClick={() => chooseResult(item)}
                                                    style={{
                                                        width: "100%",
                                                        border: "none",
                                                        background: active ? "#eef8f4" : "transparent",
                                                        display: "grid",
                                                        gridTemplateColumns: "34px minmax(0, 1fr) auto",
                                                        alignItems: "center",
                                                        gap: 10,
                                                        padding: "9px 16px",
                                                        cursor: "pointer",
                                                        textAlign: "left",
                                                    }}
                                                >
                                                    <span style={{ width: 30, height: 30, borderRadius: 6, background: "#0C3B3C", color: "#F4F1EA", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800 }}>
                                                        {group.abbr}
                                                    </span>
                                                    <span style={{ minWidth: 0 }}>
                                                        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#122A26", fontSize: 13, fontWeight: 700 }}>{item.title}</span>
                                                        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#64748b", fontSize: 12, marginTop: 2 }}>{item.subtitle}</span>
                                                    </span>
                                                    <span style={{ color: "#64748b", fontSize: 11, whiteSpace: "nowrap" }}>{formatDate(item.occurredAt)}</span>
                                                </button>
                                            );
                                        })}
                                    </section>
                                );
                            })}
                        </div>

                        {hasResults && !deep && (
                            <div style={{ borderTop: "1px solid #e2e8f0", padding: 10, display: "flex", justifyContent: "center" }}>
                                <button
                                    type="button"
                                    onClick={() => setDeep(true)}
                                    style={{ border: "1px solid #0C3B3C", borderRadius: 6, background: "white", color: "#0C3B3C", padding: "7px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                                >
                                    Recherche approfondie
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
